import { jsxs, jsx } from "react/jsx-runtime";
import { css } from "@/styled-system/css";
import { Box } from "@zillow/constellation-10";
import { event } from "@zillow/universal-analytics";
import { yieldCallback } from "@zillow/yield-callback";
import { AnimateSharedLayout, motion } from "framer-motion";
import React, { useState } from "react";
import triggerTabClickEvent from "../../utils/clickstream/tab-click.esm.js";
import CarouselDot from "./CarouselDot.esm.js";
import CarouselItem from "./CarouselItem.esm.js";
import CarouselNavigation from "./CarouselNavigation.esm.js";
import { wrap } from "../../node_modules/popmotion/dist/es/utils/wrap.esm.js";
const sliderClass = css({
  width: "100%",
  display: "flex",
  flexDirection: "row"
});
const Carousel = ({
  children,
  showTabs = true,
  showDots = true,
  showArrows = true,
  id = ""
}) => {
  const [page, setPage] = useState(0);
  const slideCount = React.Children.count(children);
  const getSlideIndex = (targetPage) => wrap(0, slideCount, targetPage);
  const activeSlideIndex = getSlideIndex(page);
  const triggerClickStreamEvent = (blockDisplayedTxt, targetPage) => event({
    // https://dataportal.zillowgroup.net/event-registry/2048
    envelope: {
      event_type_id: "2048",
      event_type_version_id: "1",
      event_template_id: "126",
      event_template_version_id: "1"
      // Master ID: 2284.2.126.1
    },
    clickstream_trigger: {
      trigger_type_nm: "interaction",
      trigger_location_nm: "cms",
      trigger_source_nm: "cms_carousel",
      trigger_object_nm: "no_trigger_object"
    },
    semantic: {
      semantic_event_nm: "cms_click"
    },
    cms_info: {
      block_id: id || "",
      block_type_cd: "carousel",
      block_displayed_txt: blockDisplayedTxt,
      // We don't want zero-based index, so add 1.
      slide_number: getSlideIndex(targetPage) + 1
    }
  });
  return /* @__PURE__ */ jsxs("div", { children: [
    showTabs && /* @__PURE__ */ jsx(AnimateSharedLayout, { children: /* @__PURE__ */ jsx(Box, { css: {
      display: "flex",
      justifyContent: "center",
      marginBottom: "default",
      flexWrap: "wrap"
    }, children: React.Children.map(children, (element, index) => React.cloneElement(element, {
      privateRenderType: "tab",
      // The below props will get passed to <CarouselTab>
      isActive: activeSlideIndex === index,
      onClick: yieldCallback(() => {
        setPage(index);
        triggerTabClickEvent({
          // React never passes `key` through props, so
          // this is always undefined; kept as-is so the
          // clickstream payload is unchanged.
          id: element.props.key,
          label: element.props.id,
          isCarouselTab: true
        });
      })
    })) }) }),
    /* @__PURE__ */ jsxs(Box, { css: {
      position: "relative",
      display: "flex",
      flexDirection: "row",
      minHeight: "300px",
      overflow: "hidden"
    }, children: [
      /* @__PURE__ */ jsx(motion.div, { className: sliderClass, animate: {
        x: `${-activeSlideIndex * 100}%`
      }, transition: {
        x: {
          type: "spring",
          stiffness: 300,
          damping: 30
        }
      }, children: React.Children.toArray(children).map((child) => React.cloneElement(child, {
        privateRenderType: "slide",
        showArrows
      })) }),
      showArrows && /* @__PURE__ */ jsx(CarouselNavigation, { onLeftButtonClick: () => setPage((currentPage) => {
        const nextPage = currentPage - 1;
        triggerClickStreamEvent("previous", nextPage);
        return nextPage;
      }), onRightButtonClick: () => setPage((currentPage) => {
        const nextPage = currentPage + 1;
        triggerClickStreamEvent("next", nextPage);
        return nextPage;
      }) })
    ] }),
    showDots && /* @__PURE__ */ jsx(Box, { css: {
      display: "flex",
      justifyContent: "center",
      flexWrap: "nowrap",
      marginTop: "default"
    }, children: React.Children.map(children, (_, index) => /* @__PURE__ */ jsx(CarouselDot, { isActive: activeSlideIndex === index, type: "button", onClick: yieldCallback(() => {
      setPage(index);
      triggerClickStreamEvent("jump_dot", index);
    }) }, index.toString())) })
  ] });
};
Carousel.Item = CarouselItem;
export {
  Carousel as default
};
